import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  servicesSnapshot,
  whyChooseUs,
  coverageCounties,
} from "../../mocks/homeData";

export default function Home() {
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState<Record<string, boolean>>({});
  const observerRefs = useRef<Map<string, HTMLElement>>(new Map());

  useEffect(() => {
    setHeroLoaded(true);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute("data-animate-id");
          if (id && entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    observerRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const setRef = (id: string) => (el: HTMLElement | null) => {
    if (el) observerRefs.current.set(id, el);
  };

  const animateClass = (id: string, base = "") =>
    `transition-all duration-700 ease-out ${base} ${
      isVisible[id] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
    }`;

  const stats = [
    { value: "500+", label: "Families Served", icon: "ri-group-line" },
    { value: "6", label: "Illinois Counties", icon: "ri-map-2-line" },
    { value: "120+", label: "Caregivers", icon: "ri-user-heart-line" },
    { value: "24/7", label: "On-Call Support", icon: "ri-24-hours-line" },
  ];

  const steps = [
    {
      step: "01",
      title: "Free Consultation",
      description:
        "Call us or fill out a form. A care advisor listens to your needs, answers questions, and explains how we can help — no pressure, no obligation.",
      icon: "ri-phone-line",
    },
    {
      step: "02",
      title: "Custom Care Plan",
      description:
        "We assess your loved one's daily routines, health needs, and preferences to design a personalized care plan that fits your family perfectly.",
      icon: "ri-file-list-3-line",
    },
    {
      step: "03",
      title: "Caregiver Match",
      description:
        "We carefully select and introduce a caregiver whose skills, personality, and schedule align with your loved one's needs and your family's values.",
      icon: "ri-user-heart-line",
    },
    {
      step: "04",
      title: "Ongoing Support",
      description:
        "Care begins, and we stay close. Regular check-ins, open communication, and plan adjustments ensure your loved one thrives every single day.",
      icon: "ri-heart-pulse-line",
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=A%20warm%20and%20compassionate%20home%20care%20scene%20showing%20a%20professional%20female%20caregiver%20gently%20assisting%20a%20smiling%20elderly%20woman%20in%20a%20bright%2C%20cozy%20living%20room%20with%20soft%20natural%20light%20streaming%20through%20windows%2C%20warm%20beige%20and%20cream%20tones%2C%20houseplants%2C%20comfortable%20furniture%2C%20conveying%20trust%20safety%20and%20comfort%2C%20editorial%20photography%20style%2C%20shallow%20depth%20of%20field&width=1600&height=700&seq=hero1&orientation=landscape"
            alt="Compassionate caregiver helping a client at home"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
        </div>

        <div
          className={`relative z-10 text-center px-4 max-w-4xl mx-auto transition-all duration-1000 ${
            heroLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <div className="mb-4 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/20">
            <div className="w-2 h-2 rounded-full bg-brand-green-400 animate-pulse" />
            <span className="text-xs font-medium text-white/90 uppercase tracking-wider">
              Trusted Home Care in Illinois
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
            Compassionate Care
            <br />
            <span className="text-brand-green-300">for Every Day</span>
          </h1>
          <p className="text-base md:text-xl text-white/85 mb-8 max-w-2xl mx-auto leading-relaxed">
            Helping you live better, every day. Compassionate, professional non-medical home care
            services for you and your loved ones across Illinois.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <Link
              to="/contact"
              className="w-full sm:w-auto px-8 py-3.5 bg-brand-green-500 hover:bg-brand-green-600 text-white font-semibold rounded-full transition-all duration-200 text-center whitespace-nowrap"
            >
              Get Care Now
            </Link>
            <Link
              to="/contact"
              className="w-full sm:w-auto px-8 py-3.5 bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white font-semibold rounded-full border border-white/30 transition-all duration-200 text-center whitespace-nowrap"
            >
              Speak to a Care Advisor
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Stats Bar */}
      <section className="py-8 md:py-10 bg-white border-b border-gray-100" data-animate-id="stats" ref={setRef("stats")}>
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {stats.map((stat, i) => (
              <div
                key={stat.label}
                className={`text-center p-4 md:p-5 rounded-xl bg-soft-gray hover:bg-brand-green-50 transition-colors duration-300 ${
                  isVisible["stats"] ? "opacity-100 scale-100" : "opacity-0 scale-95"
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                  <i className={`${stat.icon} text-xl text-brand-green-600`} />
                </div>
                <span className="text-2xl md:text-3xl font-bold text-brand-green-700 block mb-1">
                  {stat.value}
                </span>
                <span className="text-xs md:text-sm text-gray-500">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Snapshot */}
      <section className="py-14 md:py-20 bg-soft-gray" data-animate-id="services" ref={setRef("services")}>
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className={animateClass("services", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              What We Offer
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Our Care Services
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto">
              Comprehensive non-medical home care tailored to your unique needs, delivered with compassion and professionalism.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {servicesSnapshot.map((service, i) => (
              <div
                key={service.id}
                className={`group bg-white rounded-xl p-5 md:p-6 border border-gray-100 hover:border-brand-green-200 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ${
                  isVisible["services"] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-12 h-12 rounded-xl bg-brand-green-50 flex items-center justify-center mb-4 group-hover:bg-brand-green-100 transition-colors">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${service.icon} text-xl text-brand-green-600`} />
                  </div>
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-4">{service.description}</p>
                <Link
                  to="/services"
                  className="inline-flex items-center gap-1 text-sm font-medium text-brand-green-600 hover:text-brand-green-700 transition-colors"
                >
                  Learn more
                  <i className="ri-arrow-right-line text-sm" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-14 md:py-20 bg-white" data-animate-id="how-it-works" ref={setRef("how-it-works")}>
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className={animateClass("how-it-works", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Simple & Straightforward
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              How It Works
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto">
              Getting started with Sycamore Oasis takes just four simple steps. We handle the details so you can focus on what matters most.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {steps.map((item, i) => (
              <div
                key={item.step}
                className={`relative bg-soft-gray rounded-xl p-5 md:p-6 border border-gray-100 hover:border-brand-green-200 hover:shadow-md transition-all duration-300 ${
                  isVisible["how-it-works"] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-brand-green-50 flex items-center justify-center">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className={`${item.icon} text-xl text-brand-green-600`} />
                    </div>
                  </div>
                  <span className="text-3xl font-bold text-brand-green-100">{item.step}</span>
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-14 md:py-20 bg-soft-gray" data-animate-id="why" ref={setRef("why")}>
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className={animateClass("why", "order-2 lg:order-1")}>
              <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
                Why Sycamore Oasis
              </span>
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
                Trusted Care for Your Loved Ones
              </h2>
              <p className="text-sm md:text-base text-gray-500 mb-8 leading-relaxed">
                We understand that inviting a caregiver into your home is a deeply personal decision. That is why we go above and beyond to earn your trust every single day.
              </p>
              <div className="space-y-5">
                {whyChooseUs.map((item, i) => (
                  <div
                    key={item.title}
                    className={`flex items-start gap-4 group ${
                      isVisible["why"] ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"
                    } transition-all duration-500`}
                    style={{ transitionDelay: `${i * 120}ms` }}
                  >
                    <div className="w-10 h-10 rounded-lg bg-brand-green-50 flex items-center justify-center shrink-0 group-hover:bg-brand-green-100 transition-colors">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${item.icon} text-lg text-brand-green-600`} />
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-1">{item.title}</h4>
                      <p className="text-xs md:text-sm text-gray-500 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className={animateClass("why", "order-1 lg:order-2")}>
              <div className="relative rounded-2xl overflow-hidden group">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20female%20caregiver%20sitting%20beside%20a%20happy%20elderly%20man%20on%20a%20comfortable%20sofa%20in%20a%20bright%20modern%20home%2C%20both%20smiling%20warmly%2C%20natural%20daylight%2C%20cozy%20living%20room%20with%20warm%20neutral%20tones%20beige%20and%20cream%2C%20conveying%20trust%20and%20companionship%2C%20editorial%20lifestyle%20photography%2C%20soft%20warm%20lighting&width=700&height=800&seq=why1&orientation=portrait"
                  alt="Caregiver and client sharing a warm moment"
                  className="w-full h-auto object-cover rounded-2xl group-hover:scale-[1.02] transition-transform duration-700"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-brand-green-100 flex items-center justify-center">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-shield-check-line text-brand-green-600" />
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Fully Licensed & Insured</p>
                      <p className="text-xs text-gray-500">State-authorized home care agency</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Review Us Widget */}
      <section className="py-14 md:py-20 bg-white" data-animate-id="review-widget" ref={setRef("review-widget")}>
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto text-center">
          <div className={animateClass("review-widget")}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-green-50 border border-brand-green-100 mb-6">
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-sm text-gold-accent" />
                  </div>
                ))}
              </div>
              <span className="text-xs font-bold text-brand-green-700">4.9 out of 5</span>
              <span className="text-xs text-brand-green-600">· 47 Google Reviews</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              Your Feedback Matters
            </h2>
            <p className="text-sm md:text-base text-gray-500 mb-8 max-w-xl mx-auto leading-relaxed">
              If you have experienced our care, we would love to hear from you. Your review helps other families find the compassionate support they need.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
              <a
                href="https://www.google.com/maps/place/Sycamore+Oasis+Corp/@41.7290238,-88.2567613,652m/data=!3m1!1e3!4m8!3m7!1s0x17c800fd433d29:0xaa26cec045b66f09!8m2!3d41.7290238!4d-88.2567613!9m1!1b1!16s%2Fg%2F11twvlbqj3?entry=ttu"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-brand-green-600 text-white font-semibold rounded-full hover:bg-brand-green-700 transition-all duration-200 whitespace-nowrap"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-google-fill text-white" />
                </div>
                Review Us on Google
              </a>
              <Link
                to="/reviews"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white text-brand-green-800 font-semibold rounded-full border border-brand-green-200 hover:bg-brand-green-50 hover:border-brand-green-300 transition-all duration-200 whitespace-nowrap"
              >
                See All Reviews
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Area */}
      <section className="py-14 md:py-20 bg-warm-beige" data-animate-id="coverage" ref={setRef("coverage")}>
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className={animateClass("coverage", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Service Areas
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Serving Illinois Counties
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto">
              We proudly provide compassionate non-medical home care services across six counties in the greater Chicago area.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className={animateClass("coverage")}>
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-5">Counties We Serve</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {coverageCounties.map((county, i) => (
                    <div
                      key={county}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg bg-brand-green-50 hover:bg-brand-green-100 transition-colors ${
                        isVisible["coverage"] ? "opacity-100 scale-100" : "opacity-0 scale-90"
                      } transition-all duration-300`}
                      style={{ transitionDelay: `${i * 80}ms` }}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-map-pin-fill text-brand-green-600 text-xs" />
                      </div>
                      <span className="text-sm font-medium text-brand-green-800">{county}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-5 border-t border-gray-100">
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-information-line text-brand-green-600" />
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      Headquartered in Naperville, we ensure prompt response times and local expertise throughout our service region. Contact us to confirm coverage for your specific address.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className={animateClass("coverage")}>
              <div className="rounded-2xl overflow-hidden shadow-sm border border-gray-100 bg-white group">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d190601.123456789!2d-88.2!3d41.75!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x880e54c7d0e5e0e1%3A0x1234567890abcdef!2sNaperville%2C%20IL!5e0!3m2!1sen!2sus!4v1600000000000!5m2!1sen!2sus"
                  width="100%"
                  height="380"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Sycamore Oasis Service Area Map"
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 md:py-20 bg-brand-green-800" data-animate-id="cta" ref={setRef("cta")}>
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto text-center">
          <div className={animateClass("cta")}>
            <div className="w-12 h-12 rounded-full bg-brand-green-700 flex items-center justify-center mx-auto mb-4">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-heart-3-line text-2xl text-brand-green-300" />
              </div>
            </div>
            <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
              Let Us Make Your Surroundings a Peaceful Place of Rest
            </h2>
            <p className="text-sm md:text-base text-brand-green-200 mb-8 max-w-2xl mx-auto leading-relaxed">
              Every day, we help families across Illinois find comfort, dignity, and peace of mind through our compassionate non-medical home care services. Your loved one deserves the very best.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
              <Link
                to="/contact"
                className="w-full sm:w-auto px-8 py-3.5 bg-white text-brand-green-800 font-semibold rounded-full hover:bg-brand-green-50 transition-all duration-200 text-center whitespace-nowrap"
              >
                Get Care Now
              </Link>
              <a
                href="tel:+17736997894"
                className="w-full sm:w-auto px-8 py-3.5 bg-brand-green-700 text-white font-semibold rounded-full hover:bg-brand-green-600 transition-all duration-200 text-center whitespace-nowrap"
              >
                Call 773-699-7894 (24/7)
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Mini-Section */}
      <section className="py-14 md:py-20 bg-soft-gray" data-animate-id="contact" ref={setRef("contact")}>
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className={animateClass("contact", "grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start")}>
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
                Get in Touch
              </span>
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
                Ready to Start?
              </h2>
              <p className="text-sm md:text-base text-gray-500 mb-8 leading-relaxed">
                Reach out today for a free consultation. Our care advisors are ready to help you find the perfect care plan for your family.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-3 group">
                  <div className="w-10 h-10 rounded-lg bg-white border border-gray-100 flex items-center justify-center shrink-0 group-hover:border-brand-green-200 group-hover:bg-brand-green-50 transition-all">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-map-pin-line text-brand-green-600" />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Address</p>
                    <p className="text-xs md:text-sm text-gray-500">4055 S Route 59, Suite 121, Naperville, Illinois 60504</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 group">
                  <div className="w-10 h-10 rounded-lg bg-white border border-gray-100 flex items-center justify-center shrink-0 group-hover:border-brand-green-200 group-hover:bg-brand-green-50 transition-all">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-phone-line text-brand-green-600" />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Phone</p>
                    <p className="text-xs md:text-sm text-gray-500">773-699-7894 (24/7)</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 group">
                  <div className="w-10 h-10 rounded-lg bg-white border border-gray-100 flex items-center justify-center shrink-0 group-hover:border-brand-green-200 group-hover:bg-brand-green-50 transition-all">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-mail-line text-brand-green-600" />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Email</p>
                    <p className="text-xs md:text-sm text-gray-500">info@sycamoreoasis.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 group">
                  <div className="w-10 h-10 rounded-lg bg-white border border-gray-100 flex items-center justify-center shrink-0 group-hover:border-brand-green-200 group-hover:bg-brand-green-50 transition-all">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-time-line text-brand-green-600" />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Hours</p>
                    <p className="text-xs md:text-sm text-gray-500">Mon – Friday: 9:00 AM – 5:00 PM</p>
                    <p className="text-xs md:text-sm text-gray-500">24 Hours On-call</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-5">Send Us a Message</h3>
              <form
                action="https://readdy.ai/api/form/d7r72gsdj3knvn2p2ek0"
                method="POST"
                data-readdy-form
                id="contact-form-home"
                className="space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-xs font-medium text-gray-700 mb-1.5">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      required
                      className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-xs font-medium text-gray-700 mb-1.5">
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      required
                      className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all"
                      placeholder="Doe"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs font-medium text-gray-700 mb-1.5">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-xs font-medium text-gray-700 mb-1.5">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all"
                    placeholder="773-699-7894"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-xs font-medium text-gray-700 mb-1.5">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    required
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all resize-none"
                    placeholder="Tell us about your care needs..."
                  />
                  <p className="text-[11px] text-gray-400 mt-1">Maximum 500 characters</p>
                </div>
                <button
                  type="submit"
                  className="w-full px-6 py-3 bg-brand-green-600 hover:bg-brand-green-700 text-white font-semibold rounded-lg transition-all duration-200 whitespace-nowrap"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}